import { parseTag } from '../../lib/boardInvariants'
import type { RaidBoardQuery } from '../../lib/handWrittenGraphql'
import type { RaidStage, RaidStageDiff } from '../../types'

type RaidBoardStageDiffNode = RaidBoardQuery['raidBoard']['stageDiffs'][number]

/**
 * Enrich the compact wire diff with the stage/group labels already carried by
 * the same authoritative planner snapshot. Keeping this adapter isolated lets
 * the UI consume stable domain types without knowing GraphQL field casing.
 */
export function mapRaidStageDiffs(
  nodes: ReadonlyArray<RaidBoardStageDiffNode>,
  stages: ReadonlyArray<RaidStage>,
  board: RaidBoardQuery['raidBoard'],
): ReadonlyArray<RaidStageDiff> {
  const stageNames = new Map(stages.map((stage) => [stage.id, stage.name]))
  const groupNames = new Map(board.groups.map((group) => [String(group.id), group.label]))

  return nodes.map((node) => ({
    stageId: String(node.stageId),
    stageName: stageNames.get(String(node.stageId)) ?? '',
    playerId: String(node.playerId),
    playerName: node.playerLabel,
    placement: node.placement === null
      ? null
      : {
          beforeGroupId: node.placement.beforeGroupId === null ? null : String(node.placement.beforeGroupId),
          beforeGroupName: node.placement.beforeGroupId === null
            ? ''
            : groupNames.get(String(node.placement.beforeGroupId)) ?? String(node.placement.beforeGroupId),
          beforeOrderIndex: node.placement.beforeOrderIndex,
          afterGroupId: String(node.placement.afterGroupId),
          afterGroupName: groupNames.get(String(node.placement.afterGroupId)) ?? String(node.placement.afterGroupId),
          afterOrderIndex: node.placement.afterOrderIndex,
        },
    build: node.build === null
      ? null
      : {
          before: node.build.before.map((build) => ({
            orderIndex: build.orderIndex,
            weaponSlugs: [...build.weaponSlugs],
          })),
          after: node.build.after.map((build) => ({
            orderIndex: build.orderIndex,
            weaponSlugs: [...build.weaponSlugs],
          })),
          beforeActiveBuildIndex: node.build.beforeActiveBuildIndex,
          afterActiveBuildIndex: node.build.afterActiveBuildIndex,
        },
    addedTags: node.tags?.added.map(parseTag) ?? [],
    removedTags: node.tags?.removed.map(parseTag) ?? [],
  }))
}
